import { addMinutes, format, parse } from "date-fns";
import { startCase, countBy, mean, sumBy } from "lodash";
import { ResultInstance, IPlayerStats, MatchFormat, IFixture, IControl, IResult, SystemMessage, AuthPayload, ITrainingSchedule, TimeSlot, IUser } from "../definitions";
import { getUser, writeUser, writeTeamRequest, deleteTeamRequest } from "./Database";
import { getAllAwardWinners } from "./awards";
import { convertDayToLabel } from "../controls/TrainingScheduleDialog";
import { Preferences } from '@capacitor/preferences';
import type { SocialGraphicOptions } from './socialGraphics';

export const checkAndSwapScore = (score: string, result: string) => {
  const first = parseInt(score.split('-')[0]);
  const second = parseInt(score.split('-')[1]);
  if (isNaN(first) || isNaN(second)) return score;
  if (result === 'Won' && first < second) {
    return `${second}-${first}`;
  } else if (result === 'Lost' && first > second) {
    return `${second}-${first}`;
  }
  return score;
}

export const removeResultFromPlayerStats = (result: ResultInstance, players: Record<string, IPlayerStats>) => {
  Object.entries(players).forEach(player => {
    if (result.scorers?.includes(player[0])) {
      const count = result.scorers.filter(s => s === player[0]).length;
      if (!player[1].goals) player[1].goals = 0;
      player[1].goals -= count;
      if (count > 2) player[1].hatricks -= 1;
      else if (count > 1) player[1].braces -= 1;
    }
    if (result.assists?.includes(player[0])) {
      const count = result.assists.filter(s => s === player[0]).length;
      if (!player[1].assists) player[1].assists = 0;
      player[1].assists -= count;
    }
    if (result.played?.includes(player[0])) {
      player[1].games -= 1;
    }
    if (result.cleanSheet?.includes(player[0])) {
      if (player[1].cleanSheets) player[1].cleanSheets -= 1;
    }
  });
}

export const updatePlayerStats = (result: ResultInstance, playersStats: Record<string, IPlayerStats>) => {
  Object.entries(playersStats).forEach(player => {
    if (result.cleanSheet?.includes(player[0])) {
      if (player[1].cleanSheets) player[1].cleanSheets += 1;
      else player[1].cleanSheets = 1;
    }
    if (result.played?.includes(player[0])) {
      if (!player[1].games) player[1].games = 0;
      player[1].games += 1;
    }

    if (result.scorers.includes(player[0])) {
      const count = result.scorers.filter(s => s === player[0]).length;
      if (!player[1].goals) player[1].goals = 0;
      player[1].goals += count;
      if (count > 2) player[1].hatricks += 1;
      else if (count > 1) player[1].braces += 1;
    }
    if (result.assists.includes(player[0])) {
      const count = result.assists.filter(s => s === player[0]).length;
      if (!player[1].assists) player[1].assists = 0;
      player[1].assists += count;
    }
  });
}

// Helper to get a preview thumbnail (Instagram, YouTube, Twitter, TikTok, Facebook, etc.)
export const getPreview = (url: string) => {
  if (url.includes("instagram.com")) {
    // Instagram thumbnail (not official API, fallback to icon)
    return "https://upload.wikimedia.org/wikipedia/commons/a/a5/Instagram_icon.png";
  }
  if (url.includes("youtube.com") || url.includes("youtu.be")) {
    // YouTube thumbnail
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([A-Za-z0-9_-]{11})/);
    if (match) {
      return `https://img.youtube.com/vi/${match[1]}/0.jpg`;
    }
    return "https://upload.wikimedia.org/wikipedia/commons/7/75/YouTube_social_white_squircle_%282017%29.svg";
  }
  if (url.includes("twitter.com") || url.includes("x.com")) {
    return "https://upload.wikimedia.org/wikipedia/commons/6/6f/Logo_of_Twitter.svg";
  }
  if (url.includes("tiktok.com")) {
    return "https://upload.wikimedia.org/wikipedia/commons/a/a6/Tiktok_icon.svg";
  }
  if (url.includes("facebook.com")) {
    return "https://upload.wikimedia.org/wikipedia/commons/5/51/Facebook_f_logo_%282019%29.svg";
  }
  if (url.includes("threads.net")) {
    return "https://upload.wikimedia.org/wikipedia/commons/9/96/Threads_%28app%29_logo.svg";
  }
  if (url.includes("snapchat.com")) {
    return "https://upload.wikimedia.org/wikipedia/en/a/ad/Snapchat_logo.svg";
  }
  if (url.includes("linkedin.com")) {
    return "https://upload.wikimedia.org/wikipedia/commons/c/ca/LinkedIn_logo_initials.png";
  }
  // fallback generic link icon
  return "https://upload.wikimedia.org/wikipedia/commons/6/6b/Link_icon_round.png";
};

/**
 * Finds the value for a key in a Record<string, T> using case-insensitive search.
 * Returns the value if found, otherwise undefined.
 */
export function findCaseInsensitive<T>(obj: Record<string, T>, searchKey: string): T | undefined {
  if (!obj || !searchKey) return undefined;
  const foundKey = Object.keys(obj).find(
    key => key.toLowerCase() === searchKey.toLowerCase()
  );
  return foundKey ? obj[foundKey] : undefined;
}

export const formatResult = (result: ResultInstance) => `vs ${result.team} ${result.score} - ${parse(result.date, "yyyyMMdd", Date.now()).toDateString()}`;

export const isMobile = (breakpointName: string) => {
  return breakpointName === 'xsmall' || breakpointName === 'small' || breakpointName === 'medium';
}

export const toTitleCase = (phrase: string) => {
  return phrase ? startCase(phrase.toLowerCase()) : '';
}

// escape helper for injecting text into SVG
export const escapeXml = (s: string | undefined) => {
  if (!s && s !== "") return "";
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;')
    .replace(/\r\n|\r|\n/g, '&#10;');
}

export const showNextFixtureInsight = (fixtures: IFixture[],
  teams: Record<string, string[]>,
  results: ResultInstance[]): boolean => {
  if (!fixtures || fixtures.length === 0) return false;
  const upcomingFixtures = fixtures
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  if (upcomingFixtures.length > 0) {
    const nextFixture = upcomingFixtures[0];
    // Find the opponent team name nextFixture.team
    const opponentTeam =
      nextFixture.teams && nextFixture.teams.length > 0
        ? Object.keys(teams).find(team =>
          Array.isArray(teams[team]) && nextFixture.teams.includes(team)
        ) || nextFixture.teams?.[0]
        : nextFixture.teams?.[0];

    // Find all results against this team
    const resultsAgainstOpponent = results.filter(
      r => r.team?.toLowerCase() === opponentTeam?.toLowerCase()
    );
    return resultsAgainstOpponent?.length > 0 || false;
  }
  return false;
}

export function getRecords(results: ResultInstance[], players: Record<string, IPlayerStats>): Records | undefined {
  let biggestWin: StatRecord = { record: 0, results: undefined };
  let biggestLoss: StatRecord = { record: 0, results: undefined };
  let mostGoals: StatRecord = { record: 0, results: undefined };
  let mostGoalsInGame: StatRecord = { record: 0, results: undefined };
  let unbeatenStreak: StatRecord = { record: 0, results: undefined };
  let cleanSheets: StatRecord = { record: 0, results: [] };
  let currentStreak: StatRecord = { record: 0, results: [] };
  let winningStreak: StatRecord = { record: 0, results: undefined };
  let currentWinStreak: StatRecord = { record: 0, results: [] };
  let cleanSheetStreak: StatRecord = { record: 0, results: undefined };
  let currentCleanSheetStreak: StatRecord = { record: 0, results: [] };
  let highestScoringDraw: StatRecord = { record: 0, results: undefined };
  let mostGoalsConceded: StatRecord = { record: 0, results: undefined };
  let losingStreak: StatRecord = { record: 0, results: undefined };
  let currentLosingStreak: StatRecord = { record: 0, results: [] };
  let drawStreak: StatRecord = { record: 0, results: undefined };
  let currentDrawStreak: StatRecord = { record: 0, results: [] };
  let homeUnbeaten: StatRecord = { record: 0, results: undefined };
  let currentHomeUnbeaten: StatRecord = { record: 0, results: [] };
  let awayUnbeaten: StatRecord = { record: 0, results: undefined };
  let currentAwayUnbeaten: StatRecord = { record: 0, results: [] };
  let mostAssistsInGame: StatRecord = { record: 0, results: undefined };
  let nilNilDraws: StatRecord = { record: 0, results: [] };
  let highScoringGames: StatRecord = { record: 0, results: undefined };
  let runningGoalsScored = 0;
  let runningGoalsConceded = 0;
  const newPointsTrend = [0];
  const newGoalsScored: number[] = [];
  const newGoalsConceded: number[] = [];

  // Player-specific records tracking
  const playerScoringStreaks: Record<string, { current: number; max: number; results: string[] }> = {};
  const playerAssistStreaks: Record<string, { current: number; max: number; results: string[] }> = {};
  const playerAppearances: Record<string, { current: number; max: number; results: string[] }> = {};


  if (!results) return;
  results.forEach(result => {
    const goals = result.score.split('-');
    const goalsFor = parseInt(goals[0]);
    const goalsAgainst = parseInt(goals[1]);
    const totalGoals = goalsFor + goalsAgainst;
    const isHome = result.venue?.toLowerCase() === 'home';
    const isAway = result.venue?.toLowerCase() === 'away';

    // Clean sheets
    if (result.result !== 'L' && goalsAgainst === 0) {
      cleanSheets.record += 1;
      cleanSheets.results?.push(formatResult(result));
      currentCleanSheetStreak.record += 1;
      currentCleanSheetStreak.results?.push(formatResult(result));
    } else {
      if (currentCleanSheetStreak.record > cleanSheetStreak.record) cleanSheetStreak = { ...currentCleanSheetStreak };
      currentCleanSheetStreak = { record: 0, results: [] };
    }

    runningGoalsScored += goalsFor;
    runningGoalsConceded += goalsAgainst;
    newGoalsScored.push(runningGoalsScored / (newGoalsScored.length + 1));
    newGoalsConceded.push(runningGoalsConceded / (newGoalsConceded.length + 1));
    if (result.result === 'W') newPointsTrend.push(newPointsTrend[newPointsTrend.length - 1] + 3);
    else if (result.result === 'D') newPointsTrend.push(newPointsTrend[newPointsTrend.length - 1] + 1);
    else newPointsTrend.push(newPointsTrend[newPointsTrend.length - 1]);

    // Unbeaten streak
    if (result.result !== 'L') {
      currentStreak.record += 1;
      currentStreak.results?.push(formatResult(result));
    } else {
      if (currentStreak.record > unbeatenStreak.record) unbeatenStreak = { ...currentStreak };
      currentStreak = { record: 0, results: [] };
    }

    // Winning streak
    if (result.result === 'W') {
      currentWinStreak.record += 1;
      currentWinStreak.results?.push(formatResult(result));
    } else {
      if (currentWinStreak.record > winningStreak.record) winningStreak = { ...currentWinStreak };
      currentWinStreak = { record: 0, results: [] };
    }

    // Losing streak
    if (result.result === 'L') {
      currentLosingStreak.record += 1;
      currentLosingStreak.results?.push(formatResult(result));
    } else {
      if (currentLosingStreak.record > losingStreak.record) losingStreak = { ...currentLosingStreak };
      currentLosingStreak = { record: 0, results: [] };
    }

    // Draw streak
    if (result.result === 'D') {
      currentDrawStreak.record += 1;
      currentDrawStreak.results?.push(formatResult(result));
    } else {
      if (currentDrawStreak.record > drawStreak.record) drawStreak = { ...currentDrawStreak };
      currentDrawStreak = { record: 0, results: [] };
    }

    // Home unbeaten streak
    if (isHome && result.result !== 'L') {
      currentHomeUnbeaten.record += 1;
      currentHomeUnbeaten.results?.push(formatResult(result));
    } else if (isHome) {
      if (currentHomeUnbeaten.record > homeUnbeaten.record) homeUnbeaten = { ...currentHomeUnbeaten };
      currentHomeUnbeaten = { record: 0, results: [] };
    }

    // Away unbeaten streak
    if (isAway && result.result !== 'L') {
      currentAwayUnbeaten.record += 1;
      currentAwayUnbeaten.results?.push(formatResult(result));
    } else if (isAway) {
      if (currentAwayUnbeaten.record > awayUnbeaten.record) awayUnbeaten = { ...currentAwayUnbeaten };
      currentAwayUnbeaten = { record: 0, results: [] };
    }

    // Highest scoring draw
    if (result.result === 'D' && totalGoals > highestScoringDraw.record) {
      highestScoringDraw = { record: totalGoals, results: [formatResult(result)] };
    } else if (result.result === 'D' && totalGoals === highestScoringDraw.record) {
      highestScoringDraw.results?.push(formatResult(result));
    }

    // Most goals conceded in a game
    if (goalsAgainst > mostGoalsConceded.record) {
      mostGoalsConceded = { record: goalsAgainst, results: [formatResult(result)] };
    } else if (goalsAgainst === mostGoalsConceded.record) {
      mostGoalsConceded.results?.push(formatResult(result));
    }

    // High-scoring games (most combined goals)
    if (totalGoals > highScoringGames.record) {
      highScoringGames = { record: totalGoals, results: [formatResult(result)] };
    } else if (totalGoals === highScoringGames.record) {
      highScoringGames.results?.push(formatResult(result));
    }

    // Nil-nil draws
    if (result.result === 'D' && goalsFor === 0 && goalsAgainst === 0) {
      nilNilDraws.record += 1;
      nilNilDraws.results?.push(formatResult(result));
    }


    // Biggest win
    if (goalsFor - goalsAgainst > biggestWin.record) biggestWin = { record: goalsFor - goalsAgainst, results: [formatResult(result)] };
    else if (goalsFor - goalsAgainst === biggestWin.record) biggestWin.results?.push(formatResult(result));

    // Biggest loss
    if (goalsAgainst - goalsFor > biggestLoss.record) biggestLoss = { record: goalsAgainst - goalsFor, results: [formatResult(result)] };
    else if (goalsAgainst - goalsFor === biggestLoss.record) biggestLoss.results?.push(formatResult(result));

    // Most goals in a game
    if (goalsFor > mostGoals.record) mostGoals = { record: goalsFor, results: [formatResult(result)] };
    else if (goalsFor === mostGoals.record) mostGoals.results?.push(formatResult(result));

    // Most goals by a player in a game
    const scorerMap = result.scorers ? countBy(result.scorers) : undefined;
    if (scorerMap) {
      const maxRecord = Object.entries(scorerMap).reduce((max, record) => players[record[0]] && record[1] > max ? record[1] : max, 0);
      const scorers = Object.entries(scorerMap).filter(pair => pair[1] === maxRecord && players[pair[0]]);

      if (maxRecord > mostGoalsInGame.record) mostGoalsInGame = { record: maxRecord, results: scorers.map(scorer => `${scorer[0]} (${maxRecord}) - ${formatResult(result)}`) };
      else if (maxRecord === mostGoalsInGame.record) scorers.map(scorer => `${scorer[0]} (${maxRecord}) - ${formatResult(result)}`).forEach(p => mostGoalsInGame.results?.push(p));
    }

    // Most assists by a player in a game
    const assistMap = result.assists ? countBy(result.assists) : undefined;
    if (assistMap) {
      const maxRecord = Object.entries(assistMap).reduce((max, record) => players[record[0]] && record[1] > max ? record[1] : max, 0);
      const assisters = Object.entries(assistMap).filter(pair => pair[1] === maxRecord && players[pair[0]]);

      if (maxRecord > mostAssistsInGame.record) mostAssistsInGame = { record: maxRecord, results: assisters.map(assister => `${assister[0]} (${maxRecord}) - ${formatResult(result)}`) };
      else if (maxRecord === mostAssistsInGame.record) assisters.map(assister => `${assister[0]} (${maxRecord}) - ${formatResult(result)}`).forEach(p => mostAssistsInGame.results?.push(p));
    }

    // Player scoring streaks
    if (result.scorers) {
      Object.keys(players).forEach(playerName => {
        if (!playerScoringStreaks[playerName]) {
          playerScoringStreaks[playerName] = { current: 0, max: 0, results: [] };
        }

        const scored = result.scorers?.some(s => s?.toLowerCase() === playerName.toLowerCase());
        if (scored) {
          playerScoringStreaks[playerName].current += 1;
          playerScoringStreaks[playerName].results.push(formatResult(result));
        } else {
          if (playerScoringStreaks[playerName].current > playerScoringStreaks[playerName].max) {
            playerScoringStreaks[playerName].max = playerScoringStreaks[playerName].current;
          }
          playerScoringStreaks[playerName].current = 0;
          playerScoringStreaks[playerName].results = [];
        }
      });
    }

    // Player assist streaks
    if (result.assists) {
      Object.keys(players).forEach(playerName => {
        if (!playerAssistStreaks[playerName]) {
          playerAssistStreaks[playerName] = { current: 0, max: 0, results: [] };
        }

        const assisted = result.assists?.some(a => a?.toLowerCase() === playerName.toLowerCase());
        if (assisted) {
          playerAssistStreaks[playerName].current += 1;
          playerAssistStreaks[playerName].results.push(formatResult(result));
        } else {
          if (playerAssistStreaks[playerName].current > playerAssistStreaks[playerName].max) {
            playerAssistStreaks[playerName].max = playerAssistStreaks[playerName].current;
          }
          playerAssistStreaks[playerName].current = 0;
          playerAssistStreaks[playerName].results = [];
        }
      });
    }

    // Player appearance streaks (Iron Person)
    if (result.played) {
      Object.keys(players).forEach(playerName => {
        if (!playerAppearances[playerName]) {
          playerAppearances[playerName] = { current: 0, max: 0, results: [] };
        }

        const played = result.played?.some(p => p?.toLowerCase() === playerName.toLowerCase());
        if (played) {
          playerAppearances[playerName].current += 1;
          playerAppearances[playerName].results.push(formatResult(result));
        } else {
          if (playerAppearances[playerName].current > playerAppearances[playerName].max) {
            playerAppearances[playerName].max = playerAppearances[playerName].current;
          }
          playerAppearances[playerName].current = 0;
          playerAppearances[playerName].results = [];
        }
      });
    }
  })

  // Finalize current streaks
  if (currentStreak.record > unbeatenStreak.record) unbeatenStreak = { ...currentStreak };
  if (currentWinStreak.record > winningStreak.record) winningStreak = { ...currentWinStreak };
  if (currentCleanSheetStreak.record > cleanSheetStreak.record) cleanSheetStreak = { ...currentCleanSheetStreak };
  if (currentLosingStreak.record > losingStreak.record) losingStreak = { ...currentLosingStreak };
  if (currentDrawStreak.record > drawStreak.record) drawStreak = { ...currentDrawStreak };
  if (currentHomeUnbeaten.record > homeUnbeaten.record) homeUnbeaten = { ...currentHomeUnbeaten };
  if (currentAwayUnbeaten.record > awayUnbeaten.record) awayUnbeaten = { ...currentAwayUnbeaten };

  // Finalize player streaks
  Object.keys(players).forEach(playerName => {
    if (playerScoringStreaks[playerName]?.current > playerScoringStreaks[playerName]?.max) {
      playerScoringStreaks[playerName].max = playerScoringStreaks[playerName].current;
    }
    if (playerAssistStreaks[playerName]?.current > playerAssistStreaks[playerName]?.max) {
      playerAssistStreaks[playerName].max = playerAssistStreaks[playerName].current;
    }
    if (playerAppearances[playerName]?.current > playerAppearances[playerName]?.max) {
      playerAppearances[playerName].max = playerAppearances[playerName].current;
    }
  });

  // Find best scoring streak
  const bestScoringStreak = Object.entries(playerScoringStreaks).reduce((best, [name, data]) => {
    if (data.max > best.record) return { record: data.max, results: [`${name} (${data.max} games)`] };
    if (data.max === best.record) best.results?.push(`${name} (${data.max} games)`);
    return best;
  }, { record: 0, results: [] } as StatRecord);

  // Find best assist streak
  const bestAssistStreak = Object.entries(playerAssistStreaks).reduce((best, [name, data]) => {
    if (data.max > best.record) return { record: data.max, results: [`${name} (${data.max} games)`] };
    if (data.max === best.record) best.results?.push(`${name} (${data.max} games)`);
    return best;
  }, { record: 0, results: [] } as StatRecord);

  // Find iron person (most consecutive appearances)
  const ironPerson = Object.entries(playerAppearances).reduce((best, [name, data]) => {
    if (data.max > best.record) return { record: data.max, results: [`${name} (${data.max} games)`] };
    if (data.max === best.record) best.results?.push(`${name} (${data.max} games)`);
    return best;
  }, { record: 0, results: [] } as StatRecord);

  // Count POTM awards
  const potmCounts: Record<string, number> = {};
  results.forEach(r => {
    if (r.playerOfTheMatch && players[r.playerOfTheMatch]) {
      potmCounts[r.playerOfTheMatch] = (potmCounts[r.playerOfTheMatch] || 0) + 1;
    }
  });
  const mostPOTM = Object.entries(potmCounts).reduce((best, [name, count]) => {
    if (count > best.record) return { record: count, results: [`${name} (${count} awards)`] };
    if (count === best.record) best.results?.push(`${name} (${count} awards)`);
    return best;
  }, { record: 0, results: [] } as StatRecord);

  return {
    cleanSheets,
    biggestWin,
    biggestLoss,
    mostGoals,
    unbeatenStreak,
    mostGoalsInGame,
    winningStreak,
    cleanSheetStreak,
    highestScoringDraw,
    mostGoalsConceded,
    losingStreak,
    drawStreak,
    homeUnbeaten,
    awayUnbeaten,
    mostAssistsInGame,
    bestScoringStreak,
    bestAssistStreak,
    mostPOTM,
    ironPerson,
    nilNilDraws,
    highScoringGames,
    goalsConceded: newGoalsConceded,
    goalsScored: newGoalsScored,
    pointsTrend: newPointsTrend
  };
}


export interface StatRecord {
  record: number;
  results?: string[];
}

export interface Records {
  cleanSheets: StatRecord;
  biggestWin: StatRecord;
  biggestLoss: StatRecord;
  mostGoals: StatRecord;
  mostGoalsInGame: StatRecord;
  unbeatenStreak: StatRecord;
  winningStreak: StatRecord;
  cleanSheetStreak: StatRecord;
  highestScoringDraw: StatRecord;
  mostGoalsConceded: StatRecord;
  losingStreak: StatRecord;
  drawStreak: StatRecord;
  homeUnbeaten: StatRecord;
  awayUnbeaten: StatRecord;
  mostAssistsInGame: StatRecord;
  bestScoringStreak: StatRecord;
  bestAssistStreak: StatRecord;
  mostPOTM: StatRecord;
  ironPerson: StatRecord;
  nilNilDraws: StatRecord;
  highScoringGames: StatRecord;
  goalsConceded: number[];
  goalsScored: number[];
  pointsTrend: number[];
}

export const chartCardColors = [
  "#ffb74d", // orange
  "#4dd0e1", // teal // 
  "#f06292", // pink
  "#81c784", // green // blue text
  "#a755ebff", // yellow
  "#64b5f6", // blue // white text
  "#ff8a65", // deep orange
  "#f44336", // red
  "#bf3ed6ff", // purple
];

export const getFormattedResult = (result: ResultInstance, team: string) => {
  return `${team} ${result.result.toUpperCase()} ${result.score}`
}

export const getDifficultyIcon = (difficulty: number) => difficulty < 0.75 ? '💪' : (difficulty > 1.5 ? '😱' : '😐');

/**
 * Parses an "other result" line as stored in the opposition teams list, e.g. "Sky Blues W 2-1".
 * Team names may contain spaces, so the outcome and score are taken from the end of the line.
 */
export const parseOtherResultLine = (raw: unknown): { teamName: string; outcome: string; score: string } | null => {
  if (typeof raw !== 'string') return null;
  const match = raw.trim().match(/^(.+?)\s+([WDL])\s+(\d+\s*-\s*\d+)$/i);
  if (!match) return null;
  return {
    teamName: match[1].trim(),
    outcome: match[2].toUpperCase(),
    score: match[3].replace(/\s/g, ''),
  };
}

/**
 * The same result as recorded from the other team's side.
 * `A W 2-1` filed under B mirrors to `B L 1-2` filed under A.
 */
export const mirrorOtherResultLine = (filedUnder: string, raw: unknown): string | null => {
  const parsed = parseOtherResultLine(raw);
  if (!parsed || !filedUnder?.trim()) return null;
  const outcome = parsed.outcome === 'W' ? 'L' : parsed.outcome === 'L' ? 'W' : 'D';
  const [ours, theirs] = parsed.score.split('-');
  return `${filedUnder.trim()} ${outcome} ${theirs}-${ours}`;
}

/**
 * Renames `from` to `into` across all other-team results: lines filed under `from` move under `into`,
 * and lines naming `from` as the team now name `into`. Lines that would leave a team playing itself are dropped.
 */
export const mergeOtherTeamResults = (teams: Record<string, string[]>, from: string, into: string): Record<string, string[]> => {
  const norm = (name: unknown) => typeof name === 'string' ? name.trim().toLowerCase() : '';
  const fromKey = norm(from);
  const target = into.trim();
  const targetKey = norm(target);
  if (!fromKey || !targetKey || fromKey === targetKey) return teams;
  const existingTargetOwner = Object.keys(teams ?? {}).find(k => norm(k) === targetKey) ?? target;

  const merged: Record<string, string[]> = {};
  Object.entries(teams ?? {}).forEach(([owner, lines]) => {
    const ownerKey = norm(owner) === fromKey ? existingTargetOwner : owner;
    (Array.isArray(lines) ? lines : []).forEach(raw => {
      const parsed = parseOtherResultLine(raw);
      let line = raw;
      if (parsed && norm(parsed.teamName) === fromKey) line = `${target} ${parsed.outcome} ${parsed.score}`;
      const lineTeam = parsed && norm(parsed.teamName) === fromKey ? targetKey : norm(parsed?.teamName);
      if (lineTeam && lineTeam === norm(ownerKey)) return;
      (merged[ownerKey] ??= []).push(line);
    });
  });
  return merged;
}

export const getFormIcon = (selectedTeam: string, teams: Record<string, string[]>, results: ResultInstance[], extraResults: ResultInstance[] = []) => {
  const difficulty = getFormDifficulty(selectedTeam, teams, results, extraResults);
  return getDifficultyIcon(difficulty);
}

export const getFormDifficulty = (selectedTeam: string, teams: Record<string, string[]>, results: ResultInstance[], extraResults: ResultInstance[] = []) => {
  const teamKey = (selectedTeam ?? '').trim().toLowerCase();
  const form = (teams[selectedTeam] ? teams[selectedTeam].map(r => parseOtherResultLine(r)?.outcome ?? '') : [])
    .concat((results ? results.filter(r => typeof r?.team === 'string' && r.team.trim().toLowerCase() === teamKey) : []).map(r => r.result))
    .concat(extraResults.map(r => r.result));
  const scores = form.map(r => {
    switch (r) {
      case 'W': return 0;
      case 'D': return 1;
      default: return 2;
    }
  });
  return Math.round(mean(scores) || 0);
}

export const getWeekDifficulty = (selectedTeams: string[], teams: Record<string, string[]>, results: ResultInstance[]) => {
  const forms = selectedTeams.map(team => getFormDifficulty(team, teams, results));
  const ave = mean(forms) || 0;
  return getDifficultyIcon(ave);
}


export async function getSessionMatchType(): Promise<MatchFormat> {
  const { value } = await Preferences.get({ key: 'matchType' });
  const val = value as MatchFormat;
  return val === '5v5' || val === '6v6' || val === '7v7' || val === '9v9' || val === '11v11' ? val : '5v5';
}

export async function getSessionFormation(): Promise<string> {
  const { value } = await Preferences.get({ key: 'formation' });
  const matchType = await getSessionMatchType();
  return value ?? DEFAULT_FORMATION[matchType];
}

export const FORMATIONS: Record<MatchFormat, string[]> = {
  '5v5': ['1-2-1', '2-1-1', '2-2', '3-1', '1-3', '1-3w', '1-2w-1', '3w-1', '2-2w', '1-1-1-1'].sort(),
  '6v6': ['1-2-2', '2-2-1', '1-3-1', '2-1-2', '1-1-3', '3-1-1', '2-3', '1-4'].sort(),
  '7v7': ["3-1-2", "2-1-3", "3-3", '1-3-2', '1-4-1', '2-2-2', '2-3-1', '3-2-1', '4-1-1', '1-3w-2', '1-3-2w', '2-2w-2', '2-3w-1', '3-2w-1', '1-1-3-1', '1-1-3w-1'].sort(),
  '9v9': ['2-3-2', '3-2-2', '2-4-1', '3-3-1', '1-3-3', '4-2-1', '2-2-3', '3-4', '4-3', '2-2-2-1'].sort(),
  '11v11': ["4-4-2", "4-3-3", "3-4-3", "5-3-2", "4-1-4-1", "4-2-3-1", "4-3-1-2", "4-1-3-2", "3-5-2", "3-4-1-2", "3-4-2-1", "3-6-1", "5-4-1", "4-2-4", "4-3-2-1", "3-3-4"].sort(),
};

export const DEFAULT_FORMATION: Record<MatchFormat, string> = {
  '5v5': '2-2',
  '6v6': '2-2-1',
  '7v7': '2-2-2',
  '9v9': '3-2-2',
  '11v11': '4-4-2',
};
// Helper to convert image to base64 (supports PNG/JPG/SVG)
const base64Cache: Record<string, string> = {};

const getBase64Image = (imgUrl: string, retries = 3): Promise<string> =>
  new Promise((resolve, reject) => {
    if (base64Cache[imgUrl]) {
      resolve(base64Cache[imgUrl]);
      return;
    }
    const img = new window.Image();
    img.crossOrigin = "Anonymous";
    img.src = imgUrl;
    img.onload = function () {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");
        if (!ctx) throw new Error("Canvas context not available");
        ctx.drawImage(img, 0, 0);
        const dataUrl = canvas.toDataURL("image/png");
        base64Cache[imgUrl] = dataUrl;
        resolve(dataUrl);
      } catch (err) {
        reject(err);
      }
    };
    img.onerror = function (exception) {
      if (retries > 0) {
        // Retry after a short delay
        setTimeout(() => {
          getBase64Image(imgUrl, retries - 1).then(resolve).catch(reject);
        }, 300);
      } else {
        reject(new Error("Failed to load image: " + imgUrl));
      }
    };
  });

export const createResultImageUrl = async (result: IResult, control: IControl, options?: SocialGraphicOptions): Promise<string> => {
  const { createSocialGraphic } = await import('./socialGraphics');
  return createSocialGraphic({ kind: 'result', result }, control, options);
};

export const createFixtureImageUrl = async (fixture: IFixture, control: IControl, options?: SocialGraphicOptions): Promise<string> => {
  const { createSocialGraphic } = await import('./socialGraphics');
  return createSocialGraphic({ kind: 'fixture', fixture }, control, options);
};



export const handleDownloadFixtureImage = async (fixture: IFixture, control: IControl) => {
  const { SOCIAL_IMAGE_SIZE } = await import('./socialGraphics');
  const url = await createFixtureImageUrl(fixture, control);
  const img = new window.Image();
  img.onload = function () {
    const canvas = document.createElement("canvas");
    canvas.width = SOCIAL_IMAGE_SIZE;
    canvas.height = SOCIAL_IMAGE_SIZE;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(img, 0, 0, SOCIAL_IMAGE_SIZE, SOCIAL_IMAGE_SIZE);
    URL.revokeObjectURL(url);

    // Download the canvas as PNG
    const link = document.createElement("a");
    link.download = `fixture_${fixture.date}.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
  };
  img.onerror = function () {
    alert("Failed to generate image. Please try again.");
    URL.revokeObjectURL(url);
  };
  img.src = url;
};

function wrapText(text: string, maxLen: number): string[] {
  if (!text) return [];
  const words = text?.split(' ');
  const lines: string[] = [];
  let line = '';
  words.forEach(word => {
    if ((line + word).length > maxLen) {
      lines.push(line.trim());
      line = '';
    }
    line += word + ' ';
  });
  if (line) lines.push(line.trim());
  return lines;
}

export const validateControlSettings = (control: IControl, playerStats: Record<string, IPlayerStats>): { errors: SystemMessage[], warnings: SystemMessage[] } => {
  const errors: SystemMessage[] = [];
  const warnings: SystemMessage[] = [];
  const isEmpty = (v: any) => v === undefined || v === null || (typeof v === 'string' && v.trim() === '');

  // Exact required property checks
  if (isEmpty(control.teamName)) {
    errors.push({ id: 'teamName_required', message: 'Team Name is required.' });
  } else if (typeof control.teamName === 'string' && control.teamName.trim().length > 100) {
    errors.push({ id: 'teamName_too_long', message: 'Team name is too long (max 100 characters).' });
  }

  if (isEmpty(control.badge)) {
    warnings.push({ id: 'badge_missing', message: 'A Team Badge will really help others identify your team and improve how your team page looks.' });
  } else {
    const badge = control.badge;
    if (typeof badge !== 'string' || !/^(https?:\/\/|data:image\/|gs:\/\/|\/)/i.test(badge)) {
      errors.push({ id: 'badge_invalid', message: 'Team badge must be a valid URL, data URI or local path.' });
    }
  }

  if (control.badge && isEmpty(control.teamPhoto)) {
    warnings.push({ id: 'teamPhoto_missing', message: 'A Team Photo improve how your team page looks.' });
  } else if (control.badge) {
    const teamPhoto = control.teamPhoto;
    if (typeof teamPhoto !== 'string' || !/^(https?:\/\/|data:image\/|gs:\/\/|\/)/i.test(teamPhoto)) {
      errors.push({ id: 'teamPhoto_invalid', message: 'Team photo must be a valid URL, data URI or local path.' });
    }
  }

  // meetTimeOffset must be present and a non-negative number
  if (control.meetTimeOffset && isNaN(Number(control.meetTimeOffset))) {
    errors.push({ id: 'meetTimeOffset_not_number', message: 'Minutes Before Kick Off meet time must be a number.' });
  } else if (control.meetTimeOffset) {
    const offsetNum = Number(control.meetTimeOffset);
    if (!isFinite(offsetNum) || offsetNum < 0) errors.push({ id: 'meetTimeOffset_negative', message: 'Minutes Before Kick Off meet time must be a non-negative number.' });
  }

  // primaryColor required + hex validation
  const isHexColor = (v: any) => typeof v === 'string' && /^#([0-9A-F]{3}){1,2}$/i.test(v);
  if (!isEmpty(control.primaryColor) && !isHexColor(control.primaryColor)) {
    errors.push({ id: 'primaryColor_invalid', message: 'Primary Colour must be a valid hex color (e.g. #007FFF).' });
  }

  // Secondary color variants - validate if provided, require at least one variant
  const resultRownthchild = control.resultRownthchild;
  if (!isEmpty(resultRownthchild) && !isHexColor(resultRownthchild)) errors.push({ id: 'resultRownthchild_invalid', message: 'Secondary Colour must be a valid hex color (e.g. #3399FF).' });

  // uuid and ownerUid required and must be strings
  if (isEmpty(control.uuid)) {
    errors.push({ id: 'uuid_required', message: 'Uuid is required.' });
  } else if (typeof control.uuid !== 'string') {
    errors.push({ id: 'uuid_invalid', message: 'Uuid must be a string.' });
  }

  if (isEmpty(control.ownerUid)) {
    errors.push({ id: 'ownerUid_required', message: 'OwnerUid is required.' });
  } else if (typeof control.ownerUid !== 'string') {
    errors.push({ id: 'ownerUid_invalid', message: 'OwnerUid must be a string.' });
  }

  // Optional email validation if present
  if (control.email) {
    const email = control.email;
    if (typeof email !== 'string' || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.push({ id: 'email_invalid', message: 'Email is not a valid email address.' });
    }
  }

  // Specific optional string fields validation (exact properties)
  if (isEmpty(control.town)) {
    warnings.push({ id: 'town_missing', message: 'A Town will help others find your team. It will also improve your search ranking.' });
  } else if (isEmpty(control.county)) {
    warnings.push({ id: 'county_missing', message: 'A County will help others find your team. It will also improve your search ranking.' });
  } else if (isEmpty(control.country)) {
    warnings.push({ id: 'country_missing', message: 'A Country will help others find your team. It will also improve your search ranking.' });
  }

  if (isEmpty(control.league)) {
    warnings.push({ id: 'league_missing', message: 'A League will help others find your team. It will also improve your search ranking.' });
  } else if (isEmpty(control.division)) {
    warnings.push({ id: 'division_missing', message: 'A Division will help others find your team. It will also improve your search ranking.' });
  }

  // check all players have a name and valid number and img url is valid if present
  if (control.players) {
    if (Object.keys(control.players).length === 0) {
      warnings.push({ id: 'players_missing', message: 'You have no players defined. Adding players will improve how your team page looks.' });
    }

    Object.entries(control.players).forEach((player, index) => {
      if (playerStats && Object.keys(playerStats).length > 0 && !Object.keys(playerStats).includes(player[0])) return; // skip validation for players not in stats (e.g. imported from old control format)
      const playerName = player[0];
      const playerData = player[1];
      if (isEmpty(playerName)) {
        errors.push({ id: `player_${index}_name_missing`, message: `Player ${index} is missing a name.` });
      }
      if (playerData.number !== undefined && (isNaN(Number(playerData.number)) || Number(playerData.number) < 0)) {
        errors.push({ id: `player_${index}_number_invalid`, message: `Player "${playerName}" has an invalid number. It must be a non-negative number.` });
      } else if (playerData.number === undefined) {
        warnings.push({ id: `player_${index}_number_missing`, message: `Player "${playerName}" will look better with a squad number.` });
      }
      if (playerData.img !== undefined && playerData.img !== '' &&
        (typeof playerData.img !== 'string' || !/^(https?:\/\/|data:image\/|gs:\/\/|\/)/i.test(playerData.img))) {
        errors.push({ id: `player_${index}_img_invalid`, message: `Player "${playerName}" has an invalid img URL. It must be a valid URL, data URI or local path.` });
      } else if (isEmpty(playerData.img)) {
        warnings.push({ id: `player_${index}_img_missing`, message: `Player "${playerName}" will look better with a player image.` });
      }
    });
  } else {
    warnings.push({ id: 'players_missing', message: 'You have no players defined. You need players to enter reports.' });
  }

  // Return unique errors
  return { errors: Array.from(new Set(errors)), warnings: Array.from(new Set(warnings)) };
}

export const createSeasonReport = (
  name: string,
  player: IPlayerStats,
  control: IControl,
  results: ResultInstance[]
): string => {
  const teamName = control.teamName || "Your Team";
  const seasonYear = (new Date()).getFullYear().toString();

  // Helper: get if player played in a result
  const playedIn = (result: ResultInstance) =>
    result.played?.some(p => p.toLowerCase() === name.toLowerCase());

  // Trends
  let goalsTrend: number[] = [];
  let assistsTrend: number[] = [];
  let cleanSheetsTrend: number[] = [];
  let teamResultsTrend: string[] = [];
  let playerGames: number = 0;
  let playerGoals: number = 0;
  let playerAssists: number = 0;
  let playerCleanSheets: number = 0;
  let teamWinsWithPlayer = 0;
  let teamGamesWithPlayer = 0;
  let teamWinsWithoutPlayer = 0;
  let teamGamesWithoutPlayer = 0;

  // Build trends
  results.forEach((result, idx) => {
    const played = playedIn(result);
    if (played) playerGames++;
    const goals = result.scorers?.filter(scorer => scorer?.toLowerCase() === name.toLowerCase()).length ?? 0;
    const assists = result.assists?.filter(assist => assist?.toLowerCase() === name.toLowerCase()).length ?? 0;
    const cleanSheet = result.cleanSheet?.some(p => p.toLowerCase() === name.toLowerCase()) ? 1 : 0;

    playerGoals += goals;
    playerAssists += assists;
    playerCleanSheets += cleanSheet;

    goalsTrend.push(playerGoals);
    assistsTrend.push(playerAssists);
    cleanSheetsTrend.push(playerCleanSheets);

    teamResultsTrend.push(result.result);

    if (played) {
      teamGamesWithPlayer++;
      if (result.result === 'W') teamWinsWithPlayer++;
    } else {
      teamGamesWithoutPlayer++;
      if (result.result === 'W') teamWinsWithoutPlayer++;
    }
  });

  // Analyze periods
  function getPeriodTrend(trend: number[]) {
    if (trend.length < 2) return "No clear trend.";
    const diffs = trend.map((v, i, arr) => i > 0 ? v - arr[i - 1] : 0).slice(1);
    const avg = diffs.reduce((a, b) => a + b, 0) / diffs.length;
    if (avg > 0.5) return "Improving";
    if (avg < -0.5) return "Declining";
    return "Stable";
  }

  const goalsPeriodTrend = getPeriodTrend(goalsTrend);
  const assistsPeriodTrend = getPeriodTrend(assistsTrend);
  const cleanSheetsPeriodTrend = getPeriodTrend(cleanSheetsTrend);

  // Team form trend
  const teamFormTrend = (() => {
    const winCount = teamResultsTrend.filter(r => r === 'W').length;
    const drawCount = teamResultsTrend.filter(r => r === 'D').length;
    const lossCount = teamResultsTrend.filter(r => r === 'L').length;
    if (winCount > lossCount) return "Winning";
    if (lossCount > winCount) return "Struggling";
    return "Mixed";
  })();

  // Correlation between player form and team form
  function correlate(trend: number[], teamTrend: string[]) {
    // Compare periods where player scored/assisted/clean sheet and team won
    let matches = 0;
    let total = 0;
    for (let i = 0; i < trend.length; i++) {
      if (i === 0) continue;
      const playerImproved = trend[i] > trend[i - 1];
      const teamWon = teamTrend[i] === 'W';
      if (playerImproved && teamWon) matches++;
      total++;
    }
    if (total === 0) return "No correlation";
    const ratio = matches / total;
    if (ratio > 0.5) return "Player's form strongly contributed to team wins.";
    if (ratio > 0.2) return "Player's form sometimes contributed to team wins.";
    return "Little correlation between player's form and team wins.";
  }

  const goalsCorrelation = correlate(goalsTrend, teamResultsTrend);
  const assistsCorrelation = correlate(assistsTrend, teamResultsTrend);
  const cleanSheetsCorrelation = correlate(cleanSheetsTrend, teamResultsTrend);

  // Did team perform better with player?
  const winRateWithPlayer = teamGamesWithPlayer ? (teamWinsWithPlayer / teamGamesWithPlayer) : 0;
  const winRateWithoutPlayer = teamGamesWithoutPlayer ? (teamWinsWithoutPlayer / teamGamesWithoutPlayer) : 0;
  let teamPerformanceSummary = "";
  if (winRateWithPlayer > winRateWithoutPlayer) {
    teamPerformanceSummary = `The team performed better with ${name} in the lineup (${(winRateWithPlayer * 100).toFixed(1)}% win rate vs ${(winRateWithoutPlayer * 100).toFixed(1)}% without).`;
  } else if (winRateWithPlayer < winRateWithoutPlayer) {
    teamPerformanceSummary = `The team performed better without ${name} (${(winRateWithoutPlayer * 100).toFixed(1)}% win rate vs ${(winRateWithPlayer * 100).toFixed(1)}% with).`;
  } else {
    teamPerformanceSummary = `The team's win rate was similar with or without ${name}.`;
  }

  // Did player's form drop coincide with team form drop?
  let formDropCoincidence = "";
  const playerFormDrops = goalsTrend.map((v, i, arr) => i > 0 && v < arr[i - 1] ? i : -1).filter(i => i !== -1);
  const teamFormDrops = teamResultsTrend.map((r, i, arr) => i > 0 && r === 'L' && arr[i - 1] !== 'L' ? i : -1).filter(i => i !== -1);
  const coincidingDrops = playerFormDrops.filter(i => teamFormDrops.includes(i));
  if (coincidingDrops.length > 0) {
    formDropCoincidence = `There were ${coincidingDrops.length} periods where ${name}'s form drop coincided with team losses.`;
  } else {
    formDropCoincidence = `No clear coincidence between ${name}'s form drops and team losses.`;
  }

  // Awards
  const awards = results.filter(r => r.playerOfTheMatch
    && control.players
    && (r.playerOfTheMatch.toLowerCase() === name.toLowerCase()
      || getAllAwardWinners(r.otherAwards).includes(name))).length;

  // Compose report
  const report = `Season Report for ${teamName} - ${seasonYear}
Games Played: ${player.games || 0}
Goals Scored: ${player.goals || 0}
Assists: ${player.assists || 0}
Clean Sheets: ${player.cleanSheets || 0}
Man of the Match Awards: ${awards || 0}

Trend Analysis:
- Goals trend: ${goalsPeriodTrend}
- Assists trend: ${assistsPeriodTrend}
- Clean sheets trend: ${cleanSheetsPeriodTrend}
- Team form: ${teamFormTrend}

Impact Analysis:
- ${goalsCorrelation}
- ${assistsCorrelation}
- ${cleanSheetsCorrelation}
- ${teamPerformanceSummary}
- ${formDropCoincidence}

`;

  return report;
};



export const persistFollowed = (list: string[], auth: AuthPayload) => {
  try {
    const uid = auth.user?.uid;
    if (!uid) return;
    getUser(uid, undefined).then(user => {
      // A failed/slow read returns null or the empty-array sentinel, not a real
      // record. Then write only the list being persisted (plus identity fields for
      // a possible first-time record) — including requestIds: [] here would wipe
      // the user's stored follow requests on a slow connection.
      const loaded = user && !Array.isArray(user) ? user : null;
      const base = loaded ? { ...loaded, uid } : {
        uid,
        displayName: auth.user?.displayName || '',
        email: auth.user?.email || '',
        createdAt: new Date().toISOString(),
      };
      writeUser({ ...base, uid, followIds: list } as IUser);
    });
  } catch (e) {
    console.error('Failed to persist followed teams', e);
  }
};

export const persistRequested = (list: string[], addition: boolean, teamUid: string, auth: AuthPayload) => {
  try {
    const uid = auth.user?.uid;
    if (!uid) return;
    getUser(uid, undefined).then(user => {
      // If no DB record exists yet (e.g. first-time Google sign-in user who hasn't
      // created their own team) create a minimal record so displayName is available
      // when the team owner looks up the follow request. A failed/slow read also
      // lands here (null or the empty-array sentinel), so the minimal record must
      // NOT include followIds — writing followIds: [] would wipe every followed
      // team. Omitting it lets the merge preserve whatever is stored.
      const loaded = user && !Array.isArray(user) ? user : null;
      const base = loaded ? { ...loaded, uid } : {
        uid,
        displayName: auth.user?.displayName || '',
        email: auth.user?.email || '',
        createdAt: new Date().toISOString(),
      };
      writeUser({ ...base, uid, requestIds: list } as IUser);
    });
    // also persist on the team side to be approved later
    if (addition)
      writeTeamRequest(teamUid, uid, false); // add as false as not yet approved
    else
      deleteTeamRequest(teamUid, uid);
  } catch (e) {
    console.error('Failed to persist follow request', e);
  }
};


export function loadImageAsync(src: string, crossOrigin?: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    if (crossOrigin) img.crossOrigin = crossOrigin;
    img.src = src;
    img.onload = () => resolve(img);
    img.onerror = reject;
  });
}

/**
 * Loads an image for drawing to a canvas that will later be exported.
 *
 * A CORS request is attempted first because only that produces an untainted canvas, but
 * it is abandoned on failure: Firebase Storage serves no Access-Control-Allow-Origin
 * header unless the bucket has a CORS config, and a crossOrigin image from such a host
 * fails to load entirely rather than loading without CORS.
 */
export async function loadImageForCanvasAsync(src: string): Promise<HTMLImageElement> {
  if (src.startsWith('data:') || src.startsWith('blob:')) return await loadImageAsync(src);
  try {
    return await loadImageAsync(src, 'anonymous');
  } catch {
    return await loadImageAsync(src);
  }
}

export function getFixtureGoogleCalendarLink(fixture: IFixture, control: IControl) {
  const dateObj = parse(fixture.date, "yyyyMMdd", Date.now());
  const startDate = format(dateObj, "yyyyMMdd");
  let startTime = "T090000";
  let endTime = "T100000";
  if (fixture.time) {
    let meetTimeStr = 'tbc';
    try {
      meetTimeStr = format(addMinutes(parse(fixture.time, 'hh:mm', new Date()), (control.meetTimeOffset ?? 15) * -1), 'hh:mm');
    } catch (error) { }
    const [h, m] = meetTimeStr.split(":").map(Number);
    if (!isNaN(h) && !isNaN(m)) {
      startTime = `T${String(h).padStart(2, "0")}${String(m).padStart(2, "0")}00`;
      endTime = `T${String(h + 1).padStart(2, "0")}${String(m).padStart(2, "0")}00`;
    }
  }
  const details = [
    fixture.teams?.join(", "),
    fixture.venue,
    fixture.matchType,
    fixture.matchFormat,
    fixture.url
  ].filter(Boolean).join(" | ");
  const text = encodeURIComponent(`Football Fixture: ${fixture.teams?.join(" vs ")}`);
  const location = encodeURIComponent(fixture.url || "");
  const desc = encodeURIComponent(details);
  const dates = `${startDate}${startTime}/${startDate}${endTime}`;
  return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${text}&dates=${dates}&details=${desc}&location=${location}`;
}

export function getFixtureICalContent(fixture: IFixture, control: IControl) {
  const dateObj = parse(fixture.date, "yyyyMMdd", Date.now());
  const startDate = format(dateObj, "yyyyMMdd");
  let startTime = "090000";
  let endTime = "100000";
  if (fixture.time) {
    let meetTimeStr = 'tbc';
    try {
      meetTimeStr = format(addMinutes(parse(fixture.time, 'HH:mm', new Date()), (control.meetTimeOffset ?? 15) * -1), 'HH:mm');
    } catch (error) { }
    const [h, m] = meetTimeStr.split(":").map(Number);
    if (!isNaN(h) && !isNaN(m)) {
      startTime = `${String(h).padStart(2, "0")}${String(m).padStart(2, "0")}00`;
      endTime = `${String(h + 1).padStart(2, "0")}${String(m).padStart(2, "0")}00`;
    }
  }
  const dtStart = `${startDate}T${startTime}`;
  const dtEnd = `${startDate}T${endTime}`;
  const summary = `Football Fixture: ${fixture.teams?.join(" vs ")}`;
  const description = [
    fixture.teams?.join(", "),
    fixture.venue,
    fixture.matchType,
    fixture.matchFormat,
    fixture.url
  ].filter(Boolean).join(" | ");
  const location = fixture.venue || "";
  return (
    `BEGIN:VCALENDAR
VERSION:2.0
BEGIN:VEVENT
SUMMARY:${summary}
DESCRIPTION:${description}
LOCATION:${location}
DTSTART:${dtStart}
DTEND:${dtEnd}
END:VEVENT
END:VCALENDAR`
  );
}

export function downloadFixtureICal(fixture: IFixture, control: IControl) {
  const icalContent = getFixtureICalContent(fixture, control);
  const blob = new Blob([icalContent], { type: "text/calendar" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `fixture_${fixture.date}.ics`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function getTrainingGoogleCalendarLink(schedule: ITrainingSchedule, control: IControl) {

  try {
    const startDateObj = parse(schedule.startDate, "yyyyMMdd", Date.now());
    const endDateObj = parse(schedule.endDate, "yyyyMMdd", Date.now());
    const startDate = format(startDateObj, "yyyyMMdd");
    const endDate = format(endDateObj, "yyyyMMdd");

    // If no slots, fallback to a single event
    if (!schedule.slots || schedule.slots.length === 0) {
      const details = [schedule.location].filter(Boolean).join(" | ");
      const text = encodeURIComponent(`Football Training: ${control.teamName}`);
      const location = encodeURIComponent(schedule.location || "");
      const desc = encodeURIComponent(details);
      const dates = `${startDate}T090000/${endDate}T100000`;
      return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${text}&dates=${dates}&details=${desc}&location=${location}`;
    }

    // If slots exist, create a recurring event for each slot
    // We'll use the first slot as the main event, and add recurrence rules for the rest
    // Google Calendar supports RRULE for recurrence, but only one event per link
    // So, we generate a link for each slot and show all links (or just the first slot's link with RRULE)

    // Helper to get weekday string for RRULE (MO,TU,WE,TH,FR,SA,SU)
    const weekdayStr = ["SU", "MO", "TU", "WE", "TH", "FR", "SA"];

    // We'll return the first slot's link with RRULE, and optionally others as comments
    const links: string[] = schedule.slots.map(slot => {
      // slot.day: 0=Sunday, 1=Monday, ..., 6=Saturday
      // Find the first date matching this weekday on/after startDate
      let firstDate = new Date(startDateObj);
      while (firstDate.getDay() !== slot.day) {
        firstDate.setDate(firstDate.getDate() + 1);
      }
      const eventStartDate = format(firstDate, "yyyyMMdd");
      // Parse times
      const [h, m] = slot.startTime.split(":").map(Number);
      const [hEnd, mEnd] = slot.endTime.split(":").map(Number);
      const startTime = `T${String(h).padStart(2, "0")}${String(m).padStart(2, "0")}00`;
      const endTime = `T${String(hEnd).padStart(2, "0")}${String(mEnd).padStart(2, "0")}00`;

      // RRULE: weekly, until endDate, on the correct weekday
      const rrule = `RRULE:FREQ=WEEKLY;BYDAY=${weekdayStr[slot.day]};UNTIL=${format(endDateObj, "yyyyMMdd")}T235959Z`;

      const details = [schedule.location].filter(Boolean).join(" | ");
      const text = encodeURIComponent(`Football Training: ${control.teamName}`);
      const location = encodeURIComponent(schedule.location || "");
      const desc = encodeURIComponent(details);
      const dates = `${eventStartDate}${startTime}/${eventStartDate}${endTime}`;
      return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${text}&dates=${dates}&details=${desc}&location=${location}&recur=${encodeURIComponent(rrule)}`;
    });

    // Return the first link (or all links as an array if you want to show all)
    return links[0];
  } catch (error) {
    return '';
  }
}
export function getTrainingICalContent(schedule: ITrainingSchedule, control: IControl) {
  try {
    const startDateObj = parse(schedule.startDate, "yyyyMMdd", Date.now());
    const endDateObj = parse(schedule.endDate, "yyyyMMdd", Date.now());

    // Helper to get weekday string for RRULE (MO,TU,WE,TH,FR,SA,SU)
    const weekdayStr = ["SU", "MO", "TU", "WE", "TH", "FR", "SA"];

    // If no slots, fallback to a single event
    if (!schedule.slots || schedule.slots.length === 0) {
      const startDate = format(startDateObj, "yyyyMMdd");
      const endDate = format(endDateObj, "yyyyMMdd");
      const dtStart = `${startDate}T090000`;
      const dtEnd = `${endDate}T100000`;
      const summary = `Football Training: ${control.teamName}`;
      const description = [schedule.location].filter(Boolean).join(" | ");
      const location = schedule.location || "";
      return (
        `BEGIN:VCALENDAR
VERSION:2.0
BEGIN:VEVENT
SUMMARY:${summary}
DESCRIPTION:${description}
LOCATION:${location}
DTSTART:${dtStart}
DTEND:${dtEnd}
END:VEVENT
END:VCALENDAR`
      );
    }

    // If slots exist, create a recurring event for each slot
    // Each slot will be a VEVENT with a weekly RRULE on the correct weekday, until end date
    let events = schedule.slots.map(slot => {
      // slot.day: 0=Sunday, 1=Monday, ..., 6=Saturday
      // Find the first date matching this weekday on/after startDate
      let firstDate = new Date(startDateObj);
      while (firstDate.getDay() !== slot.day) {
        firstDate.setDate(firstDate.getDate() + 1);
      }
      const eventStartDate = format(firstDate, "yyyyMMdd");
      // Parse times
      const [h, m] = slot.startTime.split(":").map(Number);
      const [hEnd, mEnd] = slot.endTime.split(":").map(Number);
      const startTime = `${String(h).padStart(2, "0")}${String(m).padStart(2, "0")}00`;
      const endTime = `${String(hEnd).padStart(2, "0")}${String(mEnd).padStart(2, "0")}00`;

      // RRULE: weekly, until endDate, on the correct weekday
      const rrule = `FREQ=WEEKLY;BYDAY=${weekdayStr[slot.day]};UNTIL=${format(endDateObj, "yyyyMMdd")}T235959Z`;

      const summary = `Football Training: ${control.teamName}`;
      const description = [schedule.location].filter(Boolean).join(" | ");
      const location = schedule.location || "";

      return (
        `BEGIN:VEVENT
SUMMARY:${summary}
DESCRIPTION:${description}
LOCATION:${location}
DTSTART:${eventStartDate}T${startTime}
DTEND:${eventStartDate}T${endTime}
RRULE:${rrule}
END:VEVENT`
      );
    }).join('\n');

    return (
      `BEGIN:VCALENDAR
VERSION:2.0
${events}
END:VCALENDAR`
    );
  } catch (error) {
    return '';
  }
}

export function downloadTrainingICal(schedule: ITrainingSchedule, control: IControl) {
  const icalContent = getTrainingICalContent(schedule, control);
  const blob = new Blob([icalContent], { type: "text/calendar" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `training_${schedule.startDate}.ics`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export const createTrainingScheduleImageUrl = async (fixture: ITrainingSchedule, control: IControl): Promise<string> => {
  try {
    const daysSlot = fixture.slots?.map(slot => `${convertDayToLabel(slot.day)} ${slot.startTime}-${slot.endTime}`).join(", ") || "Time TBC";
    const startDateObj = parse(fixture.startDate, "yyyyMMdd", Date.now());
    const endDateObj = parse(fixture.endDate, "yyyyMMdd", Date.now());
    const startDateStr = format(startDateObj, "d MMMM yyyy");
    const endDateStr = format(endDateObj, "d MMMM yyyy");
    const venueStr = fixture.location || "";
    const venueLines = wrapText(venueStr, 30);
    const venueStr1 = venueLines[0] || "";
    const venueStr2 = venueLines[1] || "";
    const size = 600;
    const base64Bg = await getBase64Image("/assets/trainingImage.png");
    let homeBadgeBase64 = "";
    if (control.badge) {
      try {
        homeBadgeBase64 = await getBase64Image(control.badge);
      } catch {
        homeBadgeBase64 = "";
      }
    }
    const bannerBase64 = await getBase64Image("/assets/bannerLarge.png");

    // SVG markup with crisp, high-contrast text for social sharing.
    const svgMarkup = `
      <svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" xmlns="http://www.w3.org/2000/svg">
        <image href="${base64Bg}" x="0" y="0" width="${size}" height="${size}" opacity="0.95"/>
        <!-- semi-opaque black box under teams/result/scorers/assists for contrast -->
        <rect x="0" y="0" width="${size}" height="100%" fill="#000" opacity="0.45" rx="8" />
        <text x="50%" y="80" text-anchor="middle" font-size="32" font-family="Arial" font-weight="bold" fill="#fff" stroke-width="1">From: ${escapeXml(startDateStr)}</text>
        <text x="50%" y="120" text-anchor="middle" font-size="32" font-family="Arial" font-weight="bold" fill="#fff" stroke-width="1">To: ${escapeXml(endDateStr)}</text>
        <text x="50%" y="160" text-anchor="middle" font-size="22" font-family="Arial" fill="#fff" font-weight="bold">${escapeXml(venueStr1)}</text>
        <text x="50%" y="200" text-anchor="middle" font-size="22" font-family="Arial" fill="#fff" font-weight="bold">${escapeXml(venueStr2)}</text>
        <text x="50%" y="260" text-anchor="middle" font-size="22" font-family="Arial" fill="#fff" font-weight="bold">Kick Off - ${escapeXml(daysSlot)}</text>
        ${homeBadgeBase64
        ? `<image href="${homeBadgeBase64}" x="${size / 2 - 50}" y="320" width="100" height="100" />`
        : `<text x="50%" y="320" text-anchor="middle" font-size="28" font-family="Arial, sans-serif" fill="#fff" font-weight="bold">${escapeXml(control.teamName)}</text>`
      }
        <text x="50%" y="460" text-anchor="middle" font-size="28" font-family="Arial, sans-serif" fill="#fff" font-weight="bold">${escapeXml("Training")}</text>
        <rect x="10" y="10" width="${size - 20}" height="${size - 20}" fill="none" stroke="#fff" stroke-width="8"/>
        <text x="50%" y="${size - 30}" text-anchor="middle" font-size="28" font-family="Courier New, monospace" fill="#fff" stroke="darkGrey" stroke-width="1" font-weight="bold">club-roots.co.uk</text>
        <image href="${bannerBase64}" x="15" y="${size - 130}" width="${size - 30}" height="80" opacity="1"/>
      </svg>
    `;

    const svgBlob = new Blob([svgMarkup], { type: "image/svg+xml;charset=utf-8" });
    return URL.createObjectURL(svgBlob);
  } catch (error) {
    return "";
  }
};

/**
 * Returns a stable key that uniquely identifies a fixture within a team.
 * Used to link payment requests to a precise fixture rather than just a date,
 * which prevents same-date fixtures from sharing each other's payment details.
 *
 * Format: "date|time|team1,team2"
 */
export function fixtureKey(fixture: IFixture): string {
  const time = (fixture.time ?? '').toLowerCase();
  const teams = (fixture.teams ?? []).join(',');
  return `${fixture.date}|${time}|${teams}`;
}
